import { useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import {
  KeyboardAvoidingView,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { BRAND_HEADER_H, BrandPanel } from '~/components/BrandPanel';
import { Notice } from '~/components/ui';
import { useAuth } from '~/hooks/useAuth';
import { SUPPORT_MAILTO } from '~/lib/support';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * Sign in — the entry shell of frame 00.
 *
 * Two halves. The brand panel says what this app is and who it is for, because an operator
 * opening it for the first time was handed a phone, not a product tour. The form says the
 * one thing they can act on. There is no self-service signup and no password reset:
 * accounts are provisioned, so the recovery path is a person, and the support link is it.
 *
 * On a phone the two stack. From 900 px they sit side by side, which is frame 00w.
 */

/** Below this the panel sits above the form; at or above it, beside. */
const SPLIT_AT = 900;

/**
 * The breakpoint is measured from THIS SCREEN's own width, not the window's.
 *
 * They are the same thing on a phone and usually the same on a desktop browser, but not
 * always: Android split-screen and freeform windows hand an app a fraction of the display,
 * and a window-width breakpoint then lays out a two-column sign-in inside a 380 pt slot.
 * Measuring the container is also what makes the frame photographable at exactly 390.
 */

export function LoginScreen() {
  const { signIn, error, busy } = useAuth();
  const { c, t, radius, space, touch } = useTheme();
  const [width, setWidth] = useState(0);

  const onLayout = (e: LayoutChangeEvent) => {
    const next = e.nativeEvent.layout.width;
    setWidth((prev) => (prev === next ? prev : next));
  };
  const split = width >= SPLIT_AT;
  /*
    `signIn` RETHROWS after recording the error, so a caller that wants to react to a
    failure can. This screen does not need to — the message is already on screen, read
    from `error` — but it still has to consume the rejection. It did not, so every failed
    sign-in left an unhandled promise rejection in the browser: invisible in the UI, loud
    in the console, and reported to any error tracker pointed at the page.
  */
  const submit = () => {
    if (busy) return;
    void signIn().catch(() => {
      // Deliberately empty. useAuth has already put the failure in `error`, and the
      // Notice above renders it; there is nothing left for this screen to do with it.
    });
  };

  /* The heading answers "what is happening", and it is the only thing that changes. */
  const heading = busy
    ? { title: 'Signing in…', detail: 'Finishing with Microsoft — this takes a second.' }
    : {
        title: 'Sign in',
        detail: 'Use the Microsoft work account your administrator gave you.',
      };

  const form = (
    <View style={{ gap: space.lg, paddingBottom: space.xl }}>
      <View style={{ gap: space.sm }}>
        <Text style={[t.h2, { color: c.text }]}>{heading.title}</Text>
        {error ? (
          <Notice tone="stateFailed" title={error.title}>
            {error.detail}
          </Notice>
        ) : (
          <Text style={[t.body, { color: c.dim, lineHeight: 20 }]}>{heading.detail}</Text>
        )}
        {/*
          A determinate-looking bar for an indeterminate wait, exactly as the design draws
          it. It does not animate: a bar that crawls invites a second tap on a request
          that is already in flight.
        */}
        {busy ? (
          <View
            style={{
              height: 4,
              borderRadius: radius.sm,
              backgroundColor: c.s3,
              overflow: 'hidden',
            }}
          >
            <View style={{ width: '64%', height: 4, backgroundColor: c.brandPrimary }} />
          </View>
        ) : null}
      </View>
      {/*
        The CTA and the support line go while a sign-in is in flight. The design's
        reasoning is that there is nothing to act on for that second, and an enabled
        button invites a second press against a request already running. The heading and
        the bar above are what is left, and they say why.
      */}
      {busy ? null : (
        <>
          {/*
            One button, and no fields. D20 moved the credential prompt to Microsoft: this
            app never sees an email or a password — on web through a redirect, on the
            device through the system browser. An app that cannot read a password cannot
            leak one, and there is no longer a "forgot your password" for us to answer.

            Not PrimaryButton: this one stays full-strength ink, because a CTA that greys
            out mid-submit reads as a failure.
          */}
          <Pressable
            testID="login-submit"
            accessibilityRole="button"
            onPress={submit}
            style={{
              minHeight: touch.cta,
              borderRadius: radius.md,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: c.primary,
            }}
          >
            <Text style={[t.bodyLg, { fontWeight: '600', color: c.onPrimary }]}>
              {error?.offline ? 'Try again' : 'Sign in with Microsoft'}
            </Text>
          </Pressable>

          <Text style={[t.body, { color: c.dim, lineHeight: 24 }]}>
            Trouble signing in?{' '}
            {/*
              Underlined, as the frame draws it. Weight and colour alone do not say "this
              opens something", and the design system defines no link colour — so the
              underline is the only affordance this link has, and it is the one signal that
              survives both the greyscale and the glare check.
            */}
            <Text
              accessibilityRole="link"
              onPress={() => void Linking.openURL(SUPPORT_MAILTO)}
              style={{ color: c.text, fontWeight: '600', textDecorationLine: 'underline' }}
            >
              Contact support
            </Text>
          </Text>
        </>
      )}
    </View>
  );

  const footer = <Text style={[t.sub, { color: c.dim }]}>© scans.ai · Internal use only</Text>;

  /*
    No `Screen` wrapper. Screen paints one background and applies both insets to it, and
    this is the only screen with TWO grounds — the panel runs to the top edge and under the
    status bar on purpose, so the inset belongs inside the panel rather than around it.
  */
  if (split) {
    return (
      <View
        onLayout={onLayout}
        style={{ flex: 1, flexDirection: 'row', backgroundColor: c.surface }}
      >
        <View style={{ flex: 1 }}>
          <BrandPanel
            variant="column"
            washRadius={640}
            footer={
              <Text style={[t.sub, { color: c.onInverseMuted }]}>
                © scans.ai · Internal use only
              </Text>
            }
          />
        </View>
        {/*
          The same 208 header row as the panel, so "Sign in" lands on the baseline of
          "Glass capture" across the fold rather than floating near it.
        */}
        <View style={{ flex: 1, alignItems: 'center', paddingTop: BRAND_HEADER_H }}>
          <View style={{ width: '100%', maxWidth: 384, paddingHorizontal: space.lg }}>{form}</View>
        </View>
      </View>
    );
  }

  return (
    <View onLayout={onLayout} style={{ flex: 1, backgroundColor: c.surface }}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
          bounces={false}
        >
          <BrandPanel />
          <View
            style={{
              flex: 1,
              paddingHorizontal: space.lg,
              paddingTop: space.xl,
              justifyContent: 'space-between',
            }}
          >
            {form}
            <View style={{ paddingBottom: space.md }}>{footer}</View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
